/**
 * Client/server build identity helpers for post-deploy chunk recovery.
 * Vite injects __CLARITY_BUILD_COMMIT__ at build time (see vite.config.ts).
 */

import { resolveBuildCommitFromEnv } from "@shared/buildCommit";

declare const __CLARITY_BUILD_COMMIT__: string | undefined;

export const CLIENT_BUILD_STORAGE_KEY = "clarity-portal:client-build-commit";

export { resolveBuildCommitFromEnv };

/** Build commit baked into the client bundle (or unknown in unit tests). */
export function getClientBuildCommit(): string {
  try {
    if (typeof __CLARITY_BUILD_COMMIT__ === "string" && __CLARITY_BUILD_COMMIT__.trim()) {
      return __CLARITY_BUILD_COMMIT__.trim();
    }
  } catch {
    /* ignore */
  }
  return "unknown";
}

export function readStoredClientBuildCommit(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(CLIENT_BUILD_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeClientBuildCommit(commit: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(CLIENT_BUILD_STORAGE_KEY, commit);
  } catch {
    /* ignore */
  }
}

/**
 * When the loaded client build changes (after a successful reload onto a new deploy),
 * clear one-shot chunk-reload markers so future deploys can auto-recover again.
 */
export function syncClientBuildCommitMarker(clearReloadMarker: () => void): string {
  const current = getClientBuildCommit();
  const previous = readStoredClientBuildCommit();
  if (previous && previous !== current) {
    clearReloadMarker();
  }
  storeClientBuildCommit(current);
  return current;
}

export function isBuildMismatch(clientCommit: string, serverCommit: string | null | undefined): boolean {
  const c = String(clientCommit || "").trim();
  const s = String(serverCommit || "").trim();
  if (!c || !s || c === "unknown" || s === "unknown") return false;
  return c !== s;
}
